import { CatalogueGateway } from './catalogue-gateway.interface';

export const initCatalogueAPI = (apiSlice: any, gateway: CatalogueGateway) => {
  apiSlice
    .enhanceEndpoints({
      addTagTypes: [
        'Catalogue',
        'Model',
        'Equipment',
        'Finish',
        'Studio',
        'Kit',
        'Option',
        'BimCatalogue',
        'DefaultOptionFinishEquipment',
      ],
    })
    .injectEndpoints({
      endpoints: (builder: any) => ({
        getCatalogue: builder.query({
          query: gateway.getCatalogue,
          providesTags: ['Catalogue'],
        }),
        getAllModels: builder.query({
          query: gateway.getAllModels,
          providesTags: (result = [], error: any, arg: any) => [
            'Model',
            ...result.map(({ id }: { id: string }) => ({
              type: 'Model',
              id,
            })),
          ],
        }),
        getModelbyId: builder.query({
          query: gateway.getModelbyId,
          providesTags: (result: any, error: any, arg: any) => [
            { type: 'Model', id: arg },
          ],
        }),
        getAllFinishes: builder.query({
          query: gateway.getAllFinishes,
          providesTags: (result = [], error: any, arg: any) => [
            'Finish',
            ...result.map(({ id }: { id: string }) => ({
              type: 'Finish',
              id,
            })),
          ],
        }),
        getFinishById: builder.query({
          query: gateway.getFinishById,
          providesTags: (result: any, error: any, arg: any) => [
            { type: 'Finish', id: arg },
          ],
        }),
        getAllEquipments: builder.query({
          query: gateway.getAllEquipments,
          providesTags: (result = [], error: any, arg: any) => [
            'Equipment',
            ...result.map(({ id }: { id: string }) => ({
              type: 'Equipment',
              id,
            })),
          ],
        }),
        getEquipmentById: builder.query({
          query: gateway.getEquipmentById,
          providesTags: (result: any, error: any, arg: any) => [
            { type: 'Equipment', id: arg },
          ],
        }),
        getAllStudios: builder.query({
          query: gateway.getAllStudios,
          providesTags: (result = [], error: any, arg: any) => [
            'Studio',
            ...result.map(({ id }: { id: string }) => ({
              type: 'Studio',
              id,
            })),
          ],
        }),
        getStudioById: builder.query({
          query: gateway.getStudioById,
          providesTags: (result: any, error: any, arg: any) => [
            { type: 'Studio', id: arg },
          ],
        }),
        getAllKits: builder.query({
          query: gateway.getAllKits,
          providesTags: (result = [], error: any, arg: any) => [
            'Kit',
            ...result.map(({ id }: { id: string }) => ({
              type: 'Kit',
              id,
            })),
          ],
        }),
        getKitById: builder.query({
          query: gateway.getKitById,
          providesTags: (result: any, error: any, arg: any) => [
            { type: 'Kit', id: arg },
          ],
        }),
        getAllOptions: builder.query({
          query: gateway.getAllStudios,
          providesTags: (result = [], error: any, arg: any) => [
            'Option',
            ...result.map(({ id }: { id: string }) => ({
              type: 'Option',
              id,
            })),
          ],
        }),
        getOptionById: builder.query({
          query: gateway.getOptionById,
          providesTags: (result: any, error: any, arg: any) => [
            { type: 'Option', id: arg },
          ],
        }),
        syncStudios: builder.mutation({
          query: gateway.syncStudios,
          invalidatesTags: ['Studio'],
        }),
        syncModelsFinishsEquipements: builder.mutation({
          query: gateway.syncModelFinishEquipment,
          invalidatesTags: ['Model', 'Finish', 'Equipment'],
        }),
        syncOptions: builder.mutation({
          query: gateway.syncOptions,
          invalidatesTags: ['Option', 'Catalogue'],
        }),
        getBimCatalogue: builder.query({
          query: gateway.getBimCatalogue,
          providesTags: ['BimCatalogue'],
        }),
        getBimNodeCatalogue: builder.query({
          query: gateway.getBimNodeCatalogue,
          providesTags: (result: any, error: any, arg: any) => [
            { type: 'BimCatalogue', id: arg },
          ],
        }),

        addElementToBimCatalogue: builder.mutation({
          query: gateway.addElementToBimCatalogue,
          invalidatesTags: ['BimCatalogue'],
        }),
        postFileToBimCatalogue: builder.mutation({
          query: gateway.postFileToBimCatalogue,
          invalidatesTags: ['BimCatalogue'],
        }),

        getAllDefaultOptionFinishEquipment: builder.query({
          query: gateway.getAllDefaultOptionFinishEquipment,
          providesTags: ['DefaultOptionFinishEquipment'],
        }),
        postDefaultOptionFinishEquipment: builder.mutation({
          query: gateway.postDefaultOptionFinishEquipment,
          invalidatesTags: ['DefaultOptionFinishEquipment', 'Catalogue'],
        }),
        patchDefaultOptionFinishEquipment: builder.mutation({
          query: gateway.patchDefaultOptionFinishEquipment,
          invalidatesTags: ['DefaultOptionFinishEquipment', 'Catalogue'],
        }),
        deleteDefaultOptionFinishEquipment: builder.mutation({
          query: gateway.deleteDefaultOptionFinishEquipment,
          invalidatesTags: ['DefaultOptionFinishEquipment', 'Catalogue'],
        }),

        postDefaultOptionModel: builder.mutation({
          query: gateway.postDefaultOptionModel,
          invalidatesTags: ['Models', 'Catalogue'],
        }),
        patchDefaultOptionModel: builder.mutation({
          query: gateway.patchDefaultOptionModel,
          invalidatesTags: ['Models', 'Catalogue'],
        }),
        deleteDefaultOptionModel: builder.mutation({
          query: gateway.deleteDefaultOptionModel,
          invalidatesTags: ['Models', 'Catalogue'],
        }),
      }),
      overrideExisting: false,
    });
};

export interface CatalogueSliceType {
  useGetCatalogueQuery: Function;
  useGetAllModelsQuery: Function;
  useGetModelbyIdQuery: Function;
  useGetAllFinishesQuery: Function;
  useGetFinishByIdQuery: Function;
  useGetAllEquipmentsQuery: Function;
  useGetEquipmentByIdQuery: Function;
  useGetAllStudiosQuery: Function;
  useGetStudioByIdQuery: Function;
  useGetAllKitsQuery: Function;
  useGetKitByIdQuery: Function;
  useGetAllOptionsQuery: Function;
  useGetOptionByIdQuery: Function;
  useSyncStudiosMutation: Function;
  useSyncModelsFinishsEquipementsMutation: Function;
  useSyncOptionsMutation: Function;
  useGetBimCatalogueQuery: Function;
  useGetBimNodeCatalogueQuery: Function;
  useAddElementToBimCatalogueMutation: Function;
  usePostFileToBimCatalogueMutation: Function;
  useGetAllDefaultOptionFinishEquipmentQuery: Function;
  usePostDefaultOptionFinishEquipmentMutation: Function;
  usePatchDefaultOptionFinishEquipmentMutation: Function;
  useDeleteDefaultOptionFinishEquipmentMutation: Function;
  usePostDefaultOptionModelMutation: Function;
  usePatchDefaultOptionModelMutation: Function;
  useDeleteDefaultOptionModelMutation: Function;
}
